import Props from '@service-victoria/component-library/props';
import { FormikErrors, FormikTouched, FormikProps } from 'formik';
import { lazy } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import * as Yup from 'yup';

import { BusinessDetailsStep } from 'interfaces/transaction';
import { addressSchema, emptyAddress, getFields } from 'utils/address';
import * as constants from 'utils/constants';
import { doesNotContainEmoji, Regex, validateAbn } from 'utils/validations';

import next from '../YourWorkSite/meta';
import meta, { Values } from './meta';

export const SiteOperatorDetailsSchema = Yup.object().shape<BusinessDetailsStep>({
  siteAddress: addressSchema,
  officeAddress: addressSchema,
  abn: Yup.string().when('ownerBuilder', ownerBuilder => {
    if (ownerBuilder) {
      return Yup.string().notRequired();
    } else {
      return Yup.string()
        .required(constants.GenericValidationErrors.required)
        .test(
          'validAbn',
          `That doesn't look like a valid ABN. Please try again.`,
          function (abn?: string) {
            const intAbn = parseInt(abn?.replace(/\s/g, '') ?? '');
            if (isNaN(intAbn)) return false;
            return validateAbn(intAbn);
          }
        );
    }
  }),
  email: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'no-emojis',
      "That doesn't look like a valid email. Please try again.",
      doesNotContainEmoji
    )
    .matches(Regex.EMAIL, "That doesn't look like a valid email. Please try again."),
  phoneNumber: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'isValid',
      'That doesn’t look like a valid phone number. Please try again.',
      function validate(value?: string) {
        return !!value && isValidPhoneNumber(value);
      }
    ),
  locationShortCode: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .length(7, constants.GenericValidationErrors.locationCode),
  businessName: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'no-emojis',
      "That doesn't look like a valid business name. Please try again.",
      doesNotContainEmoji
    ),
  buildingClass: Yup.string().required(constants.GenericValidationErrors.required),
  name: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'no-emojis',
      "That doesn't look like a valid name. Please try again.",
      doesNotContainEmoji
    )
    .matches(Regex.NAME, `That doesn't look like a valid name. Please try again.`),
  practitionerLicense: Yup.string()
    .notRequired()
    .test(
      'no-emojis',
      "That doesn't look like a valid practitioner licence. Please try again.",
      doesNotContainEmoji
    ),
  permitNumber: Yup.string()
    .notRequired()
    .test(
      'isValid',
      'Please enter a valid building permit number',
      function isValid(value?: string) {
        if (!value) return true;
        return Regex.PERMIT_NO.test(value);
      }
    ),
  ownerBuilder: Yup.boolean().required()
});

export const initialValues: BusinessDetailsStep = {
  name: '',
  siteAddress: emptyAddress,
  officeAddress: emptyAddress,
  email: '',
  abn: '',
  phoneNumber: '',
  locationShortCode: '',
  businessName: '',
  buildingClass: '',
  ownerBuilder: false
};

export const initialErrors: FormikErrors<BusinessDetailsStep> = {};
export const initialTouched: FormikTouched<BusinessDetailsStep> = {};
export const validationSchema = Yup.object().shape<Values>({
  business: SiteOperatorDetailsSchema
}) as Yup.Schema<Values>;

export const Step: Props.Wizard.Step<Values> = {
  ...meta,
  Component: lazy(() => import('./SiteOperatorDetails')),
  validation: {
    schema: validationSchema,
    fieldNames: [
      'name',
      ...getFields('siteAddress'),
      ...getFields('officeAddress'),
      'email',
      'abn',
      'phoneNumber',
      'locationShortCode',
      'businessName',
      'buildingClass',
      'ownerBuilder'
    ].map(key => `business.${key}`)
  },
  scrollOnSubmitError: true,
  navigation: {
    normal: [
      {
        content: 'Next',
        shouldValidate: true,
        type: 'primary',
        onSubmitError: console.log,
        onClick: (
          context: Props.Wizard.StepConsumerContext<Values>,
          form: FormikProps<Values>
        ): void => {
          context.dispatch({
            type: 'GOTO_STEP',
            stepId: next.id,
            delayedLoadingMs: 500,
            values: form.values
          });
        }
      }
    ],
    order: 'descending'
  }
};
